/**
 * @file index.tsx
 *
 * @fileoverview Renders a persistant items accordion. This accordion does not remove items from the DOM when they are closed (can be useful for Seo purposes).
 */
import {
  Content,
  Header,
  Item,
  Root,
  Trigger,
  AccordionContentProps,
} from "@radix-ui/react-accordion";
import React, { useState } from "react";
import ArrowDownIcon from "@jq-icons/ArrowDown";
import FlexItems from "../../06-layout/03-flex";
import FlexItem from "../../06-layout/03-flex/02-flex-item";
import styles from "./../accordion.module.scss";
import { motion } from "framer-motion";

export type AccordionButtonProps = {
  /**
   * The contents of the accordion button.
   */
  children: React.ReactNode | string;
  /**
   * The down icon title.
   *
   * @default 'down'
   */
  iconTitle?: string;
  /**
   * If the accordion content is open the button will be inverted.
   */
  open: boolean;
};

const PersistentAccordionButton = ({
  children,
  iconTitle = "down",
  open,
  ...props
}: AccordionButtonProps) => (
  <Header
    className={styles.accordionButton}
    data-state={open ? "open" : "closed"}
    {...props}
  >
    <Header
      asChild={true}
      className={styles.accordionButton}
      data-state={open ? "open" : "closed"}
      {...props}
    >
      <Trigger>
        <FlexItems justifyContent="space-between" alignItems="end">
          <FlexItem>{children}</FlexItem>
          <FlexItem>
            <ArrowDownIcon color="gray7" title={iconTitle ?? "down"} />
          </FlexItem>
        </FlexItems>
      </Trigger>
    </Header>
  </Header>
);

export type PersistentAccordionPanelProps = AccordionContentProps & {
  children: React.ReactNode;
  open: boolean;
};

const PersistentAccordionPanel = ({
  children,
  open,
  ...props
}: PersistentAccordionPanelProps) => {
  return (
    <Content
      className={styles.accordionPanelContent}
      data-state={open ? "open" : "closed"}
      forceMount
    >
      <motion.div
        initial={open ? "opened" : "closed"}
        transition={{
          ease: "linear",
          duration: 0.3,
        }}
        variants={{
          opened: {
            height: "var(--radix-accordion-content-height)",
          },
          closed: {
            height: 0,
          },
        }}
        animate={open ? "opened" : "closed"}
      >
        {children}
      </motion.div>
    </Content>
  );
};

export interface PersistentAccordionProps {
  /**
   * If the accordion allows to be totally collapsible.
   *
   * @default "false"
   */
  collapsible?: boolean;
  /**
   * If the accordion allows multiple items open at once.
   *
   * @default "false"
   */
  multipleItems?: boolean;
  /**
   * Start with no item open initially.
   *
   * @default "false"
   */
  startClosed?: boolean;
  /**
   * The items of the accordion.
   */
  items: PersistentAccordionItem[];
}

export interface PersistentAccordionItem {
  value: string;
  title: React.ReactNode | string;
  content: React.ReactNode | string;
}

/**
 * An accordion allows showing multiple panels of information together. Usually one opens at a time but also allows multiple to be open at once.
 * Follows the ARIA spec https://www.w3.org/TR/wai-aria-practices-1.2/#accordion
 *
 * The persistent version of the accordion does not remove items from the DOM when they are closed (can be useful for Seo purposes).
 */
export const PersistentAccordion = ({
  items,
  startClosed = false,
  collapsible = false,
  multipleItems = false,
}: PersistentAccordionProps) => {
  const [openedItems, setOpenedItems] = useState<string[]>(
    startClosed ? [] : [items[0].value]
  );

  return (
    <Root
      type={multipleItems ? "multiple" : "single"}
      value={multipleItems ? openedItems : (openedItems[0] as any)}
      {...(!multipleItems ? { collapsible } : null)}
      onValueChange={(value) => {
        if (openedItems.includes(value)) {
          if (multipleItems) {
            // Collapse.
            const filteredItems = openedItems.filter(
              (openedItem) => openedItem !== value
            );

            if (collapsible && filteredItems.length > 0) {
              setOpenedItems(
                openedItems.filter((openedItem) => openedItem !== value)
              );
            }
          } else {
            collapsible && setOpenedItems([]);
          }
        } else {
          // Expand.
          setOpenedItems(value);
        }
      }}
    >
      {items.map((item) => (
        <Item key={item.value} value={item.value}>
          <PersistentAccordionButton open={openedItems.includes(item.value)}>
            {item.title}
          </PersistentAccordionButton>
          <PersistentAccordionPanel open={openedItems.includes(item.value)}>
            {item.content}
          </PersistentAccordionPanel>
        </Item>
      ))}
    </Root>
  );
};

export default PersistentAccordion;
